<template>
  <div class="po-view">
    <div class="page-header">
      <div class="header-left">
        <div class="header-info">
          <PageTitle title="Commandes fournisseurs" description="Gérez toutes vos commandes d'achat" />
        </div>
      </div>
      <div class="actions">
        <BaseButton v-permission="'purchase-orders.create'" class="primary-btn" @click="onAddOrder">
          <i class="fa-solid fa-plus"></i>
          Nouvelle commande
        </BaseButton>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <div class="header-left">
          <div class="search-box">
            <i class="fa-solid fa-search search-icon"></i>
            <input type="text" v-model="filters.search" placeholder="Rechercher par référence, fournisseur..."
              @input="debouncedFetch" />
            <BaseButton v-if="filters.search" @click="clearSearch" class="clear-btn">
              <i class="fa-solid fa-times"></i>
            </BaseButton>
          </div>
        </div>
        <div class="header-actions">
          <BaseButton variant="icon" size="sm" class="icon-btn" @click="fetchOrders" title="Actualiser">
            <i class="fa-solid fa-sync"></i>
          </BaseButton>
        </div>
      </div>

      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>Chargement des commandes...</p>
      </div>

      <div v-else>
        <div v-if="orders.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="fa-solid fa-file"></i>
          </div>
          <h3>Aucune commande trouvée</h3>
          <p v-if="filters.search">Essayez de modifier vos critères de recherche</p>
          <p v-else>Commencez par créer votre première commande fournisseur</p>
          <BaseButton v-if="!filters.search" v-permission="'purchase-orders.create'" class="create-first-btn" @click="onAddOrder">
            <i class="fa-solid fa-plus"></i>
            Créer la première commande
          </BaseButton>
        </div>

        <div v-else>
          <div class="table-container">
            <table class="orders-table">
              <thead>
                <tr>
                  <th class="col-reference">Référence</th>
                  <th class="col-supplier">Fournisseur</th>
                  <th class="col-date">Date</th>
                  <th class="col-status">Statut</th>
                  <th class="col-user">Créée par</th>
                  <th class="col-action">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="po in orders" :key="po.id" class="order-row" @click="goToDetails(po.id)">
                  <td class="col-reference">
                    <span class="reference-text">{{ po.reference }}</span>
                  </td>
                  <td class="col-supplier">
                    <span class="supplier-name">{{ po.supplier?.name || '-' }}</span>
                  </td>
                  <td class="col-date">
                    <div class="date-cell">{{ formatDate(po.order_date) }}</div>
                  </td>
                  <td class="px-4 py-4">
                    <BaseBadge :text="getStatusLabel(po.status)" size="sm" :variant="getStatusVariant(po.status)" />
                  </td>
                  <td class="col-user">
                    <span class="user-name">{{ po.user?.name || '-' }}</span>
                  </td>
                  <td class="col-action">
                    <BaseButton variant="icon" size="sm" @click.stop="goToDetails(po.id)" title="Voir les details">
                      <BaseIcon name="eye" :size="16" />
                    </BaseButton>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pagination-wrapper">
            <PaginationComponent :currentPage="pagination.page" :perPage="Number(pagination.per_page)"
              :total="pagination.total"
              :to="pagination.to || Math.min(pagination.page * Number(pagination.per_page), pagination.total)"
              :showPageNumbers="true" :maxVisiblePages="5" @page-change="goToPage" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue'
import PageTitle from '@/components/common/PageTitle.vue'
import purchaseOrderService from '@/services/purchaseOrderService'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'PurchaseOrdersView',
  components: { PageTitle, PaginationComponent },
  setup() {
    const loading = ref(false)
    const router = useRouter()

    const orders = ref([])
    const filters = ref({
      search: ''
    })
    const pagination = ref({
      page: 1,
      per_page: 10,
      total: 0,
      from: 0,
      to: 0
    })
    let debounceTimer = null

    const fetchOrders = async () => {
      try {
        loading.value = true
        const res = await purchaseOrderService.getPurchaseOrders({
          search: filters.value.search || undefined,
          per_page: pagination.value.per_page,
          page: pagination.value.page
        })
        const data = res.data
        orders.value = Array.isArray(data) ? data : (data.data || data.items || [])
        pagination.value.total = Number(data.total || (data.meta && data.meta.total) || orders.value.length) || 0
        pagination.value.per_page = Number(data.per_page || (data.meta && data.meta.per_page) || pagination.value.per_page) || 10
        pagination.value.page = Number(data.current_page || (data.meta && data.meta.current_page) || pagination.value.page) || 1
        pagination.value.from = Number(data.from || (data.meta && data.meta.from) || ((pagination.value.page - 1) * pagination.value.per_page + 1)) || 0
        pagination.value.to = Number(data.to || (data.meta && data.meta.to) || Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total)) || 0
      } catch (e) {
        console.error('Erreur chargement commandes:', e)
        orders.value = []
      } finally {
        loading.value = false
      }
    }

    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        pagination.value.page = 1
        fetchOrders()
      }, 300)
    }

    const clearSearch = () => {
      filters.value.search = ''
      pagination.value.page = 1
      fetchOrders()
    }

    const goToPage = (page) => {
      pagination.value.page = page
      fetchOrders()
    }

    const onAddOrder = () => {
      router.push({ name: 'purchase-orders-new' })
    }

    const goToDetails = (id) => {
      router.push({ name: 'purchase-order-details', params: { id } })
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    }

    const getStatusLabel = (status) => {
      const labels = {
        active: 'Active',
        inactive: 'Inactive',
        cancelled: 'Annulée',
        pending: 'En attente',
        in_progress: 'En cours',
        completed: 'Terminée',
        failed: 'Échouée',
        draft: 'Brouillon',
        new: 'Nouveau'
      }
      return labels[status] || status
    }

    const getStatusVariant = (status) => {
      const map = {
        active: 'success',
        completed: 'success',
        pending: 'warning',
        in_progress: 'warning',
        cancelled: 'danger',
        failed: 'danger',
        inactive: 'gray',
        draft: 'gray',
        new: 'info'
      }
      return map[status] || 'gray'
    }


    fetchOrders()

    return {
      loading,
      orders,
      filters,
      pagination,
      debouncedFetch,
      clearSearch,
      goToPage,
      onAddOrder,
      formatDate,
      goToDetails,
      getStatusLabel,
      getStatusVariant,
      fetchOrders
    }
  }
})
</script>

<style scoped>
.po-view {
  padding: 0 0 24px 0;
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.header-info {
  flex: 1;
}

.page-heading {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.actions {
  display: flex;
  gap: 8px;
}

.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #0A5C6B;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(10, 92, 107, 0.2);
  transition: all 0.2s;
}

.primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.primary-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}


.card {
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 2px solid #f1f5f9;
  background: #fafbfc;
  gap: 16px;
}

.search-box {
  display: flex;
  align-items: center;
  background: white;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 14px;
  min-width: 360px;
  transition: all 0.2s;
}

.search-box:focus-within {
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.search-icon {
  width: 18px;
  height: 18px;
  stroke: #94a3b8;
  stroke-width: 2;
  flex-shrink: 0;
}

.search-box input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 12px;
  font-size: 14px;
  color: #0f172a;
  background: transparent;
}

.clear-btn {
  background: #f1f5f9;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.clear-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.clear-btn svg {
  width: 16px;
  height: 16px;
  stroke-width: 2;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.icon-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.icon-btn svg {
  width: 18px;
  height: 18px;
  stroke: #475569;
  stroke-width: 2;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(10, 92, 107, 0.1);
  border-top-color: #0A5C6B;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-container p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 16px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon svg {
  width: 40px;
  height: 40px;
  stroke: #94a3b8;
  stroke-width: 2;
}

.empty-state h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.empty-state p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  max-width: 400px;
}

.create-first-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #0A5C6B;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.create-first-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(10, 92, 107, 0.3);
}

.create-first-btn svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.table-container {
  overflow-x: auto;
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
}

.orders-table thead {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
}

.orders-table th {
  padding: 16px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.orders-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #1e293b;
}

.order-row {
  cursor: pointer;
  transition: all 0.2s;
}

.order-row:hover {
  background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
}

.order-row:last-child td {
  border-bottom: none;
}

.col-reference {
  min-width: 180px;
}

.col-supplier {
  min-width: 220px;
}

.col-date {
  min-width: 140px;
}

.col-status {
  min-width: 120px;
}

.col-user {
  min-width: 160px;
}

.col-action {
  width: 60px;
  text-align: center;
}

.reference-text {
  font-weight: 700;
  color: #0f172a;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.supplier-name,
.user-name {
  font-weight: 600;
  color: #0f172a;
}

.date-cell {
  color: #64748b;
}


.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-badge.status-active {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-badge.status-active .status-dot {
  background: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.2);
}

.status-badge.status-inactive {
  background: #f5f5f5;
  color: #616161;
}

.status-badge.status-inactive .status-dot {
  background: #616161;
  box-shadow: 0 0 0 3px rgba(97, 97, 97, 0.2);
}

.status-badge.status-cancelled {
  background: #ffebee;
  color: #c62828;
}

.status-badge.status-cancelled .status-dot {
  background: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.2);
}

.action-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: linear-gradient(135deg, #0A5C6B 0%, #0e7689 100%);
  border-color: #0A5C6B;
}

.action-btn svg {
  width: 18px;
  height: 18px;
  stroke: #475569;
  stroke-width: 2;
  transition: stroke 0.2s;
}

.action-btn:hover svg {
  stroke: white;
}

.pagination-wrapper {
  padding: 20px 24px;
  border-top: 2px solid #f1f5f9;
  background: #fafbfc;
}

@media (max-width: 768px) {
  .po-view {
    padding: 0 0 16px 0;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    flex-direction: column;
    align-items: stretch;
  }

  .actions {
    width: 100%;
  }

  .primary-btn {
    width: 100%;
    justify-content: center;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: auto;
    width: 100%;
  }

  .table-container {
    overflow-x: auto;
    margin: 0 -24px;
  }

  .orders-table {
    min-width: 800px;
  }
}
</style>